import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, exhaustMap, filter, from, of, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { getApiErrorMessage } from '@ninety/platform/models';
import { DetailType } from '@ninety/platform/models/_shared/detail-type.enum';
import { Email } from '@ninety/platform/models/_shared/email';
import { User } from '@ninety/platform/models/_shared/user';
import { EmailType } from '@ninety/platform/models/directory/email-type';
import { HelperService } from '@ninety/platform/services';
import {
  DetailViewActions,
  selectCompany,
  MainLayoutIntercomActions,
  NotificationActions,
  UsersStateActions,
  selectCurrentUserId,
} from '@ninety/platform/state';
import { AvatarUploadDialogComponent } from '@ninety/ui/core';

import {
  shouldAddPrimaryEmailAsMainRow,
  UserDetailFormService,
} from '../components/user-detail/services/user-detail-form.service';
import { DirectoryService } from '../services/directory.service';

import { UserDetailActions } from './user-detail.actions';
import { UserDetailSelectors } from './user-detail.selectors';

const findMainEmail = (emails: Email[] | undefined | null): string | undefined =>
  emails?.find(e => e.typeCode === EmailType.main)?.email;

const normalizeEmail = (email: string | undefined): string => email?.trim().toLowerCase() ?? '';

/** The Main email that the detail form shows before any edit. */
export const initialMainEmail = (user: User | undefined | null): string | undefined =>
  shouldAddPrimaryEmailAsMainRow(user) ? user?.primaryEmail : findMainEmail(user?.emailAddresses);

export const shouldPatchPrimaryEmail = (
  user: User | undefined | null,
  submittedMainEmail: string | undefined,
  canEditMainEmail: boolean
): boolean =>
  !!user?.personId &&
  !!submittedMainEmail &&
  canEditMainEmail &&
  normalizeEmail(submittedMainEmail) !== normalizeEmail(initialMainEmail(user));

export const INVITE_USER_DATA = 'inviteUserData';
export const UPDATE_ROLE_DATA = 'updateRoleData';

@Injectable()
export class UserDetailEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly directoryService = inject(DirectoryService);
  private readonly dialog = inject(MatDialog);
  private readonly helperService = inject(HelperService);
  private readonly userDetailFormService = inject(UserDetailFormService);

  openUserDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.init),
      map(({ user }) =>
        DetailViewActions.opened({
          config: { pathParts: [DetailType.directory, user._id] },
          source: 'Directory',
        })
      )
    )
  );

  /** if no user is selected when detail is opened, select/fetch it */
  checkForSelectedUserOnUserDetailInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.checkForSelectedUser),
      concatLatestFrom(() => this.store.select(UserDetailSelectors.selectSelectedUser)),
      filter(([, selectedUser]) => !selectedUser),
      map(([{ userId }]) => UserDetailActions.getUserFromRoute({ userId }))
    )
  );

  getUserFromRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.getUserFromRoute),
      // Always fetch user from API since directory state may not be available globally
      map(({ userId }) => UserDetailActions.fetchUserFromRoute({ userId }))
    )
  );

  /** if the user is not in state, fetch it */
  fetchUserFromRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.fetchUserFromRoute),
      switchMap(({ userId }) =>
        this.directoryService.getUserById(userId).pipe(
          map(user => UserDetailActions.initWithUserFromRoute({ user })),
          catchError((error: unknown) => {
            return of(
              NotificationActions.notifyError({ error: error, message: 'Could not get user.  Please try again.' })
            );
          })
        )
      )
    )
  );

  buildUserDetailForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDetailActions.init, UserDetailActions.initWithUserFromRoute, UserDetailActions.cancel),
        concatLatestFrom(() => [
          this.store.select(UserDetailSelectors.selectSelectedUser),
          this.store.select(UserDetailSelectors.selectCanEditSelectedUserMetadata),
        ]),
        tap(([_, user, canEditUser]) => {
          this.userDetailFormService.buildForm(user, canEditUser);
        })
      ),
    { dispatch: false }
  );

  hideIntercom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.init, UserDetailActions.initWithUserFromRoute),
      map(() => MainLayoutIntercomActions.hideIntercom())
    )
  );

  showIntercom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.destroy),
      map(() => MainLayoutIntercomActions.showIntercom())
    )
  );

  saveUserDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.save),
      map(() => this.userDetailFormService.getUserAndMetadataUpdate()),
      concatLatestFrom(() => [
        this.store.select(UserDetailSelectors.selectSelectedUser),
        this.store.select(UserDetailSelectors.selectCanEditSelectedUserMainEmail),
      ]),
      concatMap(([formData, user, canEditMainEmail]) => {
        const userUpdate = formData.userUpdate as Partial<User>;
        const newMainEmail = findMainEmail(userUpdate.emailAddresses)?.trim();
        const mainEmailChanged = shouldPatchPrimaryEmail(user, newMainEmail, canEditMainEmail);
        // For a user with no linked person, the API sets primaryEmail to the Main address.
        // PATCH /Users/Details returns no body, so we make the same change in the store.
        const primaryEmail = mainEmailChanged || !user?.personId ? newMainEmail : undefined;

        const emailChange$ = mainEmailChanged
          ? this.directoryService.updatePersonPrimaryEmail(user.personId, newMainEmail)
          : of(null);

        return emailChange$.pipe(
          concatMap(() => this.directoryService.updateUserDetails(user, formData)),
          map(updated =>
            UserDetailActions.updateSuccess({
              _id: updated._id,
              changes: primaryEmail ? { ...updated, primaryEmail } : updated,
            })
          ),
          catchError((error: unknown) =>
            of(
              NotificationActions.notifyError({
                error: error,
                message: getApiErrorMessage(error) ?? 'Could not update user.  Please try again.',
              })
            )
          )
        );
      })
    )
  );

  saveUserDetailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.updateSuccess),
      map(({ _id, changes }) => UsersStateActions.updateSuccess({ _id, changes }))
    )
  );

  openAvatarUploadDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDetailActions.openAvatarUploadDialog),
        concatLatestFrom(() => [
          this.store.select(UserDetailSelectors.selectSelectedUser),
          this.store.select(selectCurrentUserId),
        ]),
        exhaustMap(([, user, currentUserId]) =>
          this.dialog
            .open<AvatarUploadDialogComponent, unknown, { url: string }>(AvatarUploadDialogComponent, {
              id: 'avatar-upload-dialog',
              data: {
                editeePersonMetadataId: user.personMetadataId,
                editeeUserId: user._id,
                forOtherPerson: user._id !== currentUserId,
              },
            })
            .afterClosed()
        )
      ),
    { dispatch: false }
  );

  copyInviteLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailActions.copyInviteLink),
      concatLatestFrom(() => [this.store.select(selectCompany)]),
      switchMap(([{ user }, company]) => {
        const emailAndType = user.emailAddresses.find(e => e.typeCode === EmailType.main);

        if (emailAndType) {
          const inviteLink = this.helperService.buildInviteLink(user, emailAndType.email, company);

          // Use 'from()' to handle the Promise returned by clipboard API
          return from(navigator.clipboard.writeText(inviteLink)).pipe(
            switchMap(() => {
              return of(
                NotificationActions.notify({
                  message: 'Invite link copied!',
                  duration: 3000,
                })
              );
            }),
            catchError(() => {
              return of(
                NotificationActions.showError({
                  message: 'Error in copying invite link',
                  title: 'Error',
                })
              );
            })
          );
        } else {
          return of(
            NotificationActions.showError({
              message: 'User is missing the main email. Please reinvite',
              title: 'Error',
            })
          );
        }
      }),
      catchError((error: unknown) => {
        return of(NotificationActions.notifyError({ error: error, message: 'Failed to copy Invite Link' }));
      })
    )
  );
}
